import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Mail, MailOpen, Archive, Search, RefreshCw, Tag,
  User, Building2, Phone, Clock, MessageSquare, FileText, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new:      'bg-blue-100 text-blue-700 border-blue-200',
  read:     'bg-gray-100 text-gray-500 border-gray-200',
  archived: 'bg-yellow-50 text-yellow-700 border-yellow-200',
};

const TYPE_LABELS = {
  contact: { label: 'Quick Contact', icon: MessageSquare, color: 'text-purple-600 bg-purple-50' },
  brief:   { label: 'Brief Request', icon: FileText,       color: 'text-orange-600 bg-orange-50' },
};

export default function ContractorMessages() {
  const { token } = useAuth();
  const location = useLocation();
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (filter !== 'all') params.append('status', filter);
      const res = await axios.get(`${API}/api/contractor/inbox?${params}`, { headers });
      const fetchedItems = res.data.items || [];
      setItems(fetchedItems);
      setTotal(res.data.total || 0);

      // Auto-select message from URL ?id=inq_xxx
      const urlParams = new URLSearchParams(location.search);
      const targetId = urlParams.get('id');
      if (targetId) {
        const found = fetchedItems.find(i => i.id === targetId);
        if (found) {
          setSelected(found);
          if (found.status === 'new') {
            axios.patch(`${API}/api/contractor/inbox/${found.id}`, { status: 'read' }, { headers });
            setItems(prev => prev.map(i => i.id === found.id ? { ...i, status: 'read' } : i));
          }
        }
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, filter, location.search]);

  useEffect(() => { load(); }, [load]);

  const markStatus = async (id, status) => {
    await axios.patch(`${API}/api/contractor/inbox/${id}`, { status }, { headers });
    setItems(prev => prev.map(i => i.id === id ? { ...i, status } : i));
    if (selected?.id === id) setSelected(p => ({ ...p, status }));
  };

  const selectItem = async (item) => {
    setSelected(item);
    if (item.status === 'new') markStatus(item.id, 'read');
  };

  const filtered = items.filter(i => {
    const q = search.toLowerCase();
    return !q || (i.sender?.name || '').toLowerCase().includes(q)
      || (i.sender?.email || '').toLowerCase().includes(q)
      || (i.contact?.subject || i.brief?.show_name || '').toLowerCase().includes(q);
  });

  const newCount = items.filter(i => i.status === 'new').length;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
        <div>
          <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" /> Messages
            {newCount > 0 && (
              <span className="text-xs bg-orange-500 text-white font-bold px-2 py-0.5 rounded-full">{newCount} new</span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{total} total inquiries received</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* List */}
        <div className="w-full lg:w-96 flex-shrink-0 border-r border-gray-100 flex flex-col bg-white overflow-y-auto">
          {/* Filters + Search */}
          <div className="p-3 border-b border-gray-50 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search messages..." className="pl-8 h-8 text-xs" />
            </div>
            <div className="flex gap-1.5">
              {['all','new','read','archived'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${filter === f ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Message rows */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6 py-12">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">No messages yet.<br />Inquiries from your public profile will appear here.</p>
            </div>
          ) : (
            filtered.map(item => {
              const typeInfo = TYPE_LABELS[item.inquiry_type] || TYPE_LABELS.contact;
              const TypeIcon = typeInfo.icon;
              const isSelected = selected?.id === item.id;
              const subject = item.contact?.subject || item.brief?.show_name || 'No subject';
              const preview = item.contact?.message || item.brief?.notes || '';
              return (
                <button key={item.id} onClick={() => selectItem(item)}
                  className={`w-full text-left px-4 py-3.5 border-b border-gray-50 hover:bg-orange-50/50 transition-colors ${isSelected ? 'bg-orange-50 border-l-2 border-l-orange-400' : item.status === 'new' ? 'bg-blue-50/40' : ''}`}>
                  <div className="flex items-start gap-2.5">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${typeInfo.color}`}>
                      <TypeIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <p className={`text-sm truncate ${item.status === 'new' ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                          {item.sender?.name || 'Anonymous'}
                        </p>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {new Date(item.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 truncate">{subject}</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">{preview.slice(0, 60)}</p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <span className={`text-xs border px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.status] || STATUS_COLORS.new}`}>
                          {item.status}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-300 mt-2 flex-shrink-0" />
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Detail panel */}
        <div className="flex-1 overflow-y-auto bg-[#f7f7f5]">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-8">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center">
                <MailOpen className="w-7 h-7 text-gray-300" />
              </div>
              <p className="text-gray-400 text-sm">Select a message to read it</p>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto p-6 space-y-5">
              {/* Actions */}
              <div className="flex items-center justify-between">
                <span className={`text-xs border px-2 py-1 rounded-full font-medium ${STATUS_COLORS[selected.status] || STATUS_COLORS.new}`}>
                  {selected.status}
                </span>
                <div className="flex gap-2">
                  {selected.status !== 'read' && (
                    <button onClick={() => markStatus(selected.id, 'read')}
                      className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 bg-white border border-gray-200 px-3 py-1.5 rounded-lg transition-colors">
                      <MailOpen className="w-3.5 h-3.5" /> Mark Read
                    </button>
                  )}
                  {selected.status !== 'archived' && (
                    <button onClick={() => markStatus(selected.id, 'archived')}
                      className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 bg-white border border-gray-200 px-3 py-1.5 rounded-lg transition-colors">
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  )}
                </div>
              </div>

              {/* Sender card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Sender</h3>
                <div className="space-y-2">
                  {selected.sender?.name && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-semibold text-gray-900">{selected.sender.name}</span>
                    </div>
                  )}
                  {selected.sender?.company && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building2 className="w-4 h-4 text-gray-400" /> {selected.sender.company}
                    </div>
                  )}
                  {selected.sender?.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${selected.sender.email}`} className="text-orange-600 hover:underline">{selected.sender.email}</a>
                    </div>
                  )}
                  {selected.sender?.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400" /> {selected.sender.phone}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-gray-400 pt-1">
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(selected.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                  </div>
                </div>
              </div>

              {/* Message content */}
              {selected.inquiry_type === 'contact' && selected.contact && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Message</h3>
                  {selected.contact.subject && (
                    <p className="font-bold text-gray-900 mb-3">{selected.contact.subject}</p>
                  )}
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selected.contact.message}</p>
                  <a href={`mailto:${selected.sender?.email}?subject=Re: ${encodeURIComponent(selected.contact.subject || '')}`}
                    className="mt-4 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors">
                    <Mail className="w-4 h-4" /> Reply via Email
                  </a>
                </div>
              )}

              {/* Brief content */}
              {selected.inquiry_type === 'brief' && selected.brief && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Brief Details</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                    {[
                      ['Show', selected.brief.show_name],
                      ['City', selected.brief.city],
                      ['Event Dates', selected.brief.event_dates],
                      ['Budget', selected.brief.budget],
                      ['Stand Size', selected.brief.stand_sqm ? `${selected.brief.stand_sqm} m²` : null],
                    ].filter(([,v]) => v).map(([label, val]) => (
                      <div key={label}>
                        <p className="text-xs text-gray-400 font-medium">{label}</p>
                        <p className="font-semibold text-gray-900">{val}</p>
                      </div>
                    ))}
                  </div>
                  {selected.brief.notes && (
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1">Notes</p>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selected.brief.notes}</p>
                    </div>
                  )}
                  <a href={`mailto:${selected.sender?.email}?subject=Re: Stand Inquiry - ${encodeURIComponent(selected.brief.show_name || '')}`}
                    className="mt-4 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors">
                    <Mail className="w-4 h-4" /> Reply via Email
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
